/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { useAuth } from '../../AuthContext';
import { settingsApi } from '../../api';
import { RowMenu } from '../../components/SettingGroups';
import { Empty, money, useLoader } from '../../components/common';
import { SettingsPage, Panel } from './shell';
import PackageDialog from './PackageDialog';

export function SettingPackage() {
  const { branches } = useAuth();
  const packages = useLoader(() => settingsApi.packages(), []);
  const services = useLoader(() => settingsApi.services(), []);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  return (
    <SettingsPage
      action="สร้างแพ็กเกจ"
      onAction={() => setCreating(true)}
      showInactive={showInactive}
      onShowInactive={setShowInactive}
    >
      <Panel loader={packages} empty={(d) => !d.packages.length} emptyIcon="product">
        {(d) => {
          const visiblePackages = d.packages.filter((p) => showInactive || p.is_active !== false);
          if (!visiblePackages.length) return <div className="card"><Empty icon="product" /></div>;
          return (
          /* Divs rather than a <table>: in the original the header sits on the
             page background and each record is its own white band, which is one
             set of box properties on a div but a per-cell corner-and-shadow hack
             on table rows. The roles hand the table back to a screen reader.
             --band-cols is declared once so the header and its rows cannot
             drift apart. */
          <div
            className="band-table"
            role="table"
            aria-label="แพ็กเกจ"
            /* Their grid, read off the running page: the name column is
               exactly twice the others and the ••• column is 24px. */
            style={{ '--band-cols': '2fr 1fr 1fr 1fr 1fr 24px' }}
          >
            <div className="band-head" role="row">
              <span role="columnheader">ชื่อแพ็กเกจ</span>
              <span role="columnheader">ประเภท</span>
              <span role="columnheader" className="num">ราคาขาย</span>
              <span role="columnheader" className="num">ระยะเวลาใช้งาน</span>
              <span role="columnheader">สถานะ</span>
              <span role="columnheader" />
            </div>

            {visiblePackages.map((p) => {
              const name = p.name_th || p.name_en;
              // A package that carries stored credit is แทนเงินสด; one made of
              // service quantities is จำนวนครั้ง.
              const isCredit = p.package_type === 'value'
                || p.items.some((i) => i.creditAmount != null);

              return (
                <div className="band-row" role="row" key={p.id}>
                  <span role="cell" className="band-cell band-name">{name}</span>
                  <span role="cell" className="band-cell">{isCredit ? 'แทนเงินสด' : 'จำนวนครั้ง'}</span>
                  <span role="cell" className="band-cell num">{money(p.price)}</span>
                  {/* The original prints 9999 for a package that never expires
                      rather than a phrase — see the capture. */}
                  <span role="cell" className="band-cell num">{p.validity_days ?? 9999}</span>
                  {/* Plain text, not a pill — โปรโมชั่น is the page with a pill. */}
                  <span role="cell" className={`band-cell${p.is_active ? '' : ' band-off'}`}>
                    {p.is_active ? 'ใช้งาน' : 'ปิดใช้งาน'}
                  </span>
                  <span role="cell" className="band-menu">
                    {/* DELETE retires the row rather than erasing it, so the
                        menu says so and offers the way back. */}
                    <RowMenu
                      items={[
                        { label: 'แก้ไข', onClick: () => setEditing(p) },
                        p.is_active
                          ? { label: 'ปิดใช้งาน',
                              danger: true,
                              onClick: async () => {
                                await settingsApi.remove('packages', p.id);
                                packages.reload();
                              } }
                          : { label: 'เปิดใช้งาน',
                              onClick: async () => {
                                await settingsApi.restore('packages', p.id);
                                packages.reload();
                              } },
                      ]}
                    />
                  </span>
                </div>
              );
            })}
          </div>
          );
        }}
      </Panel>

      {creating && (
        <PackageDialog
          branches={branches}
          services={services.data?.services || []}
          onSubmit={async (payload) => {
            await settingsApi.createPackage(payload);
            packages.reload();
            setCreating(false);
          }}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <PackageDialog
          packageRow={editing}
          branches={branches}
          services={services.data?.services || []}
          onSubmit={async (payload) => {
            await settingsApi.updatePackage(editing.id, payload);
            packages.reload();
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </SettingsPage>
  );
}
